import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem("bdc_token");
    if (!token) {
      setLoading(false);
      return;
    }
    // Hydrate immédiatement depuis le cache pour éviter le retour au login pendant la vérification
    try {
      const cached = JSON.parse(localStorage.getItem("bdc_user") || "null");
      if (cached) setUser(cached);
    } catch (e) { /* ignore */ }

    const tryFetch = async () => {
      const res = await api.get("/auth/me");
      setUser(res.data);
      try { localStorage.setItem("bdc_user", JSON.stringify(res.data)); } catch (e) { /* ignore */ }
    };
    try {
      await tryFetch();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        // Token réellement invalide/expiré → on déconnecte proprement
        localStorage.removeItem("bdc_token");
        localStorage.removeItem("bdc_user");
        setUser(null);
      } else {
        // Erreur réseau / timeout / 5xx (redémarrage backend, connexion mobile instable) :
        // on NE déconnecte PAS. Un petit retry, sinon on garde la session en cache.
        await new Promise((r) => setTimeout(r, 1500));
        try { await tryFetch(); } catch (e2) { /* on reste connecté avec l'utilisateur en cache */ }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from Google OAuth, callback will establish session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    loadMe();
  }, [loadMe]);

  // Offre ambassadeur privée : crédite silencieusement les bougies offertes
  // si un code valable (48h) a été ouvert via un lien direction.
  const claimAmbassador = useCallback(async (currentUser) => {
    let code = null;
    try { code = localStorage.getItem("bdc_amb_code"); } catch (e) { /* ignore */ }
    if (!code) return currentUser;
    try {
      const res = await api.post("/me/ambassador/claim", { code });
      try { localStorage.removeItem("bdc_amb_code"); } catch (e) { /* ignore */ }
      if (res.data?.ok && currentUser) {
        const updated = { ...currentUser, free_eternal_candles: res.data.free_eternal_candles };
        setUser(updated);
        return updated;
      }
    } catch (e) { /* ignore */ }
    return currentUser;
  }, []);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    localStorage.setItem("bdc_token", res.data.token);
    try { localStorage.setItem("bdc_user", JSON.stringify(res.data.user)); } catch (e) { /* ignore */ }
    setUser(res.data.user);
    await claimAmbassador(res.data.user);
    return res.data.user;
  };

  const register = async (payload) => {
    let amb = null;
    try { amb = localStorage.getItem("bdc_amb_code"); } catch (e) { /* ignore */ }
    const res = await api.post("/auth/register", { ...payload, ambassador_code: amb || undefined });
    localStorage.setItem("bdc_token", res.data.token);
    try { localStorage.setItem("bdc_user", JSON.stringify(res.data.user)); } catch (e) { /* ignore */ }
    setUser(res.data.user);
    try { if (amb) localStorage.removeItem("bdc_amb_code"); } catch (e) { /* ignore */ }
    return res.data.user;
  };

  const googleLogin = async (sessionId) => {
    const res = await api.post("/auth/google", { session_id: sessionId });
    localStorage.setItem("bdc_token", res.data.token);
    try { localStorage.setItem("bdc_user", JSON.stringify(res.data.user)); } catch (e) { /* ignore */ }
    setUser(res.data.user);
    await claimAmbassador(res.data.user);
    return res.data.user;
  };

  const logout = () => {
    localStorage.removeItem("bdc_token");
    localStorage.removeItem("bdc_user");
    Object.keys(sessionStorage).filter((k) => k.startsWith("bdc_profile_reminder_")).forEach((k) => sessionStorage.removeItem(k));
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, googleLogin, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};
